import { useState } from "react";
import { SaveNoteInput } from "@acto/gql/graphql";
import { useSaveNote } from "@acto/taskpane/core/data-access/notes/useSaveNote";
import { useSummaryGeneration } from "@acto/taskpane/core/data-access/notes/useSummaryGeneration";
import type { UseSummaryGenerationOptions } from "@acto/taskpane/core/data-access/notes/types";

/**
 * Saved Note shape returned by the `saveNote` mutation. Mirrors the selection set in
 * `useSaveNote` (core/data-access/notes/useSaveNote.tsx) — re-derived here so the recap
 * component can read `note.id` / `note.summary` without importing codegen result types.
 */
export type RecapNote = NonNullable<Awaited<ReturnType<ReturnType<typeof useSaveNote>["saveNote"]>>>;

export interface CreateRecapInput {
  clientId: string;
  content: string;
  title?: string;
  /**
   * Exactly one of: meetingId, (seriesId, occurrenceDate) for a recurring-series occurrence, or
   * adHocMeeting for an ad-hoc ("Neue Nachbereitung") recap. The ad-hoc meeting is created
   * together with the note server-side; its client-generated id makes re-commits (edits,
   * retries) upsert instead of creating a second meeting.
   */
  meetingId?: string;
  seriesId?: string;
  occurrenceDate?: string;
  adHocMeeting?: { id: string; scheduledDate: string };
}

export interface UseCreateRecapOptions {
  /** Forwarded to useSummaryGeneration — fires when the AI summary stream succeeds. */
  onSummaryComplete?: UseSummaryGenerationOptions["onComplete"];
  /** Forwarded to useSummaryGeneration — fires when the summary should refresh note data. */
  onRefetchNotes?: UseSummaryGenerationOptions["onRefetchNotes"];
}

export interface UseCreateRecapResult {
  /**
   * Single-call commit: upserts the note then triggers AI summarization for the returned
   * note id. Returns the saved note, or null on save failure.
   */
  commit: (input: CreateRecapInput) => Promise<RecapNote | null>;
  /** The saved note (with id), available after a successful commit. */
  note: RecapNote | null;
  /** True while the summary request/stream is in flight (submitting/accepted/streaming). */
  isSummarizing: boolean;
  /** True once the AI summary has succeeded (cached or freshly streamed). */
  isDone: boolean;
  /** The generated AI summary text, available once summarization succeeds (else null). */
  summaryText: string | null;
  /** The note was saved but its summary could not be generated. A failed save returns null from `commit`. */
  summarizeError: Error | null;
  /** Resets the summary-generation state (aborts any in-flight stream). */
  reset: () => void;
}

/**
 * Orchestrates the recap commit: `saveNote` -> `triggerSummary(noteId)`.
 *
 * Reuses the core `useSaveNote` mutation and the core `useSummaryGeneration` SSE client
 * verbatim — the POST /meeting_recap/summarize + event-stream logic is NOT re-ported here.
 * Surfaces a single unified status the morph/animation session can gate on:
 * `isSummarizing` (in flight) and `isDone` (succeeded).
 */
export function useCreateRecap(options: UseCreateRecapOptions = {}): UseCreateRecapResult {
  const { onSummaryComplete, onRefetchNotes } = options;

  const { saveNote } = useSaveNote();
  const summary = useSummaryGeneration({ onComplete: onSummaryComplete, onRefetchNotes });

  const [note, setNote] = useState<RecapNote | null>(null);

  const commit = async (input: CreateRecapInput): Promise<RecapNote | null> => {
    // saveNote upserts by meeting, so a re-commit with the same target overwrites the
    // note instead of inserting a duplicate.
    const saved = await saveNote({
      clientId: input.clientId,
      meetingId: input.meetingId,
      seriesId: input.seriesId,
      occurrenceDate: input.occurrenceDate,
      adHocMeeting: input.adHocMeeting,
      content: input.content,
      title: input.title,
    } satisfies SaveNoteInput);
    if (!saved) {
      // saveNote already captured the error in its own state.
      return null;
    }

    setNote(saved);

    // useSummaryGeneration owns the SSE stream lifecycle and exposes progress via
    // summarizeStatus afterwards.
    await summary.triggerSummary(saved.id);

    return saved;
  };

  return {
    commit,
    note,
    isSummarizing: summary.isPending,
    isDone: summary.status === "succeeded",
    summaryText: summary.result?.summary.text ?? null,
    summarizeError: summary.error,
    reset: summary.reset,
  };
}
